// ---------------------------------------------------------------------------
// Sprint 7 A5: Score Breakdown Component
// ---------------------------------------------------------------------------
// Shows lead score with 3 expandable categories + improvement suggestion.

import { useState } from "react";

interface ScoreFactor {
  label: string;
  points: number;
  evidence: string;
}

interface ScoreCategory {
  name: "engagement" | "fit" | "intent";
  score: number;
  maxScore: number;
  factors: ScoreFactor[];
}

interface ScoreBreakdownProps {
  totalScore: number;
  tier: "Hot" | "Warm" | "Cold";
  categories: ScoreCategory[];
  improvementSuggestion: string;
}

const TIER_CONFIG = {
  Hot: { color: "text-[var(--color-destructive)] bg-[var(--color-destructive)]/5 border-[var(--color-destructive)]/20", emoji: "🔥" },
  Warm: { color: "text-[var(--color-warning)] bg-[var(--color-warning)]/5 border-[var(--color-warning)]/20", emoji: "⏳" },
  Cold: { color: "text-[var(--color-primary)] bg-[var(--color-primary)]/5 border-[var(--color-primary)]/20", emoji: "❄️" },
};

const CATEGORY_LABELS: Record<string, string> = {
  engagement: "Engagement",
  fit: "Fit",
  intent: "Intent",
};

export function ScoreBreakdown({ totalScore, tier, categories, improvementSuggestion }: ScoreBreakdownProps) {
  const tierConfig = TIER_CONFIG[tier] ?? TIER_CONFIG.Cold;

  return (
    <div className="space-y-4">
      {/* Header: Score + Tier */}
      <div className="flex items-center gap-3">
        <div className="text-3xl font-bold text-[var(--color-foreground)]">{totalScore}</div>
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${tierConfig.color}`}>
          <span>{tierConfig.emoji}</span>
          <span>{tier}</span>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-2">
        {categories.map((category) => (
          <CategoryRow key={category.name} category={category} />
        ))}
      </div>

      {/* Improvement suggestion */}
      <div className="bg-[var(--color-primary)]/5 dark:bg-[var(--color-primary)]/20 border border-[var(--color-primary)]/20 dark:border-[var(--color-primary)]/90 rounded-lg px-3 py-2">
        <div className="text-xs font-medium text-[var(--color-primary)] dark:text-[var(--color-primary)]/70 mb-0.5">How to improve</div>
        <div className="text-sm text-[var(--color-primary)] dark:text-[var(--color-primary)]/50">{improvementSuggestion}</div>
      </div>
    </div>
  );
}

function CategoryRow({ category }: { category: ScoreCategory }) {
  const [expanded, setExpanded] = useState(false);
  const score = typeof category.score === "number" && Number.isFinite(category.score) ? category.score : 0;
  const maxScore = typeof category.maxScore === "number" && Number.isFinite(category.maxScore) && category.maxScore > 0
    ? category.maxScore
    : 1;
  const percentage = Math.min(100, Math.max(0, Math.round((score / maxScore) * 100)));

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between hover:bg-[var(--color-muted)] rounded-lg px-2 py-1.5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[var(--color-foreground)]">
            {CATEGORY_LABELS[category.name]}
          </span>
          <span className="text-xs text-[var(--color-muted-foreground)]">
            {score}/{maxScore}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                percentage >= 75 ? "bg-[var(--color-success)]/40" : percentage >= 50 ? "bg-[var(--color-warning)]/40" : "bg-[var(--color-destructive)]/40"
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className="text-xs text-[var(--color-muted-foreground)]">{expanded ? "▲" : "▼"}</span>
        </div>
      </button>

      {expanded && (
        <div className="ml-4 mt-1 space-y-1 pb-2">
          {category.factors.map((factor, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className={factor.points >= 0 ? "text-[var(--color-success)]" : "text-[var(--color-destructive)]"}>
                  {factor.points >= 0 ? "+" : ""}{factor.points}
                </span>
                <span className="text-[var(--color-muted-foreground)]">{factor.label}</span>
              </div>
              <span className="text-[var(--color-muted-foreground)] text-[10px]">{factor.evidence}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
